// Ported verbatim from the LeadMailer Design System (components/navigation/Sidebar.jsx).
// Desktop fixed sidebar; on narrow screens render via Topbar's drawer instead.
import React from 'react'

export interface NavItem {
  id: string
  label: string
  icon?: React.ReactNode
}

export interface SidebarProps {
  items?: NavItem[]
  active?: string
  onSelect?: (id: string) => void
  footer?: React.ReactNode
  /** Rendered in the flex spacer between the nav list and the footer. */
  middle?: React.ReactNode
  badgeCounts?: Record<string, number | undefined>
  style?: React.CSSProperties
}

export function Sidebar({
  items = [],
  active,
  onSelect,
  footer,
  middle,
  badgeCounts = {},
  style,
}: SidebarProps) {
  return (
    <nav
      /* Frosted: the colour comes from --surface-sidebar (re-pointed to glass
         in index.css); this class only adds the blur behind it. */
      className="lm-glass-flat"
      style={{
        width: 'var(--sidebar-w)',
        boxSizing: 'border-box',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: '12px 10px',
        background: 'var(--surface-sidebar)',
        borderRight: '1px solid var(--border-chrome)',
        ...style,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px 16px' }}>
        <span
          style={{
            width: 22,
            height: 22,
            flex: 'none',
            borderRadius: 6,
            background: 'var(--accent)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fff"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m17.5 4.5-4.9 14-2.8-6.3-6.3-2.8Z" transform="translate(1.5 1)" />
            <path d="M17.5 4.5 9.8 12.2" transform="translate(1.5 1)" />
          </svg>
        </span>
        <span
          style={{
            fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
            fontSize: 'var(--text-base)',
            letterSpacing: 'var(--tracking-tight)',
            color: 'var(--text-primary)',
          }}
        >
          LeadMailer
        </span>
      </div>
      {items.map((it) => (
        <NavItemButton
          key={it.id}
          item={it}
          active={active === it.id}
          onSelect={onSelect}
          count={badgeCounts[it.id]}
        />
      ))}
      {middle ?? <div style={{ flex: 1 }} />}
      {footer}
    </nav>
  )
}

function NavItemButton({
  item,
  active,
  onSelect,
  count,
}: {
  item: NavItem
  active: boolean
  onSelect?: (id: string) => void
  count?: number
}) {
  const [hover, setHover] = React.useState(false)
  return (
    <button
      onClick={() => onSelect && onSelect(item.id)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        minHeight: 34,
        padding: '0 10px',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-base)',
        fontWeight: (active
          ? 'var(--weight-medium)'
          : 'var(--weight-regular)') as React.CSSProperties['fontWeight'],
        textAlign: 'left',
        color: active ? 'var(--nav-active-text)' : 'var(--text-secondary)',
        background: active
          ? 'var(--nav-active-bg)'
          : hover
            ? 'var(--nav-hover-bg)'
            : 'transparent',
        // 3px accent bar on the active item; inset so nothing shifts.
        // Dark maps --nav-active-bar to transparent, keeping dark unchanged.
        boxShadow: active ? 'inset 3px 0 0 var(--nav-active-bar)' : 'none',
        transition: 'background var(--dur-fast) var(--ease-out)',
      }}
    >
      {item.icon && (
        <span
          style={{
            display: 'inline-flex',
            width: 16,
            color: active ? 'var(--nav-icon-active)' : 'var(--nav-icon)',
          }}
        >
          {item.icon}
        </span>
      )}
      <span style={{ flex: 1 }}>{item.label}</span>
      {count ? (
        <span
          style={{
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
            color: 'var(--text-muted)',
            background: 'var(--surface-sunken)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-full)',
            padding: '1px 7px',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {count}
        </span>
      ) : null}
    </button>
  )
}
